import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Undo2, ShieldQuestion, CheckCircle2, XCircle, Search, ArrowUpRight } from 'lucide-react';

import { DataTable, type Column } from '@/components/common/DataTable';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { KpiCard } from '@/components/common/KpiCard';
import { ErrorState } from '@/components/common/ErrorState';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { returnsService } from '@/services/returns';
import type { ReturnOrder, ReturnStatus } from '@/types/returns';
import { fmt } from '@/lib/format';
import { useDebounced } from '@/hooks/useDebounced';
import { ReturnRequestDetailSheet } from './ReturnRequestDetailSheet';

type RequestTab = 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'all';

// Statuses that belong on the reviewer queue (request-cycle rows).
// Post-physical-movement statuses (RECEIVED / QC_PENDING / GOOD_INV /
// BAD_INV / CLOSED) live on the /returns page so the two surfaces
// don't show the same rows.
const REQUEST_STATUSES: ReturnStatus[] = ['REQUESTED', 'APPROVED', 'REJECTED'];

/**
 * Customer-initiated return requests in REQUESTED state — the reviewer
 * queue. Symmetric to /cancel-requests. Approved + post-approval rows
 * live on the /returns page (the physical-movement view).
 */
export function ReturnRequestsPage() {
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<RequestTab>('REQUESTED');
  const [selected, setSelected] = useState<ReturnOrder | null>(null);
  const debounced = useDebounced(search);

  // Fetch all request-cycle rows (REQUESTED + APPROVED + REJECTED) in
  // one go so the tabs can carry live counts AND the "history" of
  // already-decided requests stays visible to the reviewer instead of
  // disappearing the instant they hit approve/reject.
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['returns', { surface: 'requests' }],
    // BE caps pageSize at 200 (see ListReturnsQueryDto). The request
    // queue is well below that in practice — bumping the BE cap is a
    // separate decision.
    queryFn: () => returnsService.list({ pageSize: 200 }),
    refetchInterval: 30_000,
  });

  const requestRows = useMemo(
    () => (data?.rows ?? []).filter((r) => REQUEST_STATUSES.includes(r.status)),
    [data],
  );

  const counts = useMemo(() => {
    const c = { all: requestRows.length, REQUESTED: 0, APPROVED: 0, REJECTED: 0 };
    for (const r of requestRows) {
      if (r.status === 'REQUESTED') c.REQUESTED += 1;
      else if (r.status === 'APPROVED') c.APPROVED += 1;
      else if (r.status === 'REJECTED') c.REJECTED += 1;
    }
    return c;
  }, [requestRows]);

  // Tab + search filter applied to the in-memory rows.
  const rows = useMemo(() => {
    const tabFiltered =
      tab === 'all' ? requestRows : requestRows.filter((r) => r.status === tab);
    const q = debounced.trim().toLowerCase();
    if (!q) return tabFiltered;
    return tabFiltered.filter(
      (r) =>
        r.returnNumber.toLowerCase().includes(q) ||
        (r.externalReturnRef ?? '').toLowerCase().includes(q) ||
        r.salesOrder.orderNumber.toLowerCase().includes(q) ||
        (r.customer?.name ?? '').toLowerCase().includes(q),
    );
  }, [requestRows, debounced, tab]);

  const summary = useMemo(() => {
    const pendingRows = requestRows.filter((r) => r.status === 'REQUESTED');
    const unassigned = pendingRows.filter((r) => !r.assignedReviewerId).length;
    const assigned = pendingRows.length - unassigned;
    let oldest: string | null = null;
    for (const r of pendingRows) {
      if (!oldest || r.createdAt < oldest) oldest = r.createdAt;
    }
    return {
      pending: pendingRows.length,
      unassigned,
      assigned,
      oldest,
      decidedTotal: counts.APPROVED + counts.REJECTED,
    };
  }, [requestRows, counts]);

  const tabs: StatusTabOption[] = [
    { value: 'REQUESTED', label: 'Pending review', count: counts.REQUESTED },
    { value: 'APPROVED', label: 'Approved', count: counts.APPROVED },
    { value: 'REJECTED', label: 'Rejected', count: counts.REJECTED },
    { value: 'all', label: 'All', count: counts.all },
  ];

  const columns: Column<ReturnOrder>[] = [
    {
      key: 'returnNumber',
      header: 'Return #',
      width: '170px',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-mono text-[12px] font-medium text-foreground">{r.returnNumber}</div>
          {r.externalReturnRef && (
            <div className="font-mono text-[10.5px] text-muted-foreground">{r.externalReturnRef}</div>
          )}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Requested at',
      width: '180px',
      cell: (r) => (
        <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">
          {fmt.dateTime(r.createdAt)}
        </span>
      ),
    },
    {
      key: 'order',
      header: 'Order',
      width: '170px',
      cell: (r) => (
        <span className="font-mono text-[12px] text-foreground">{r.salesOrder.orderNumber}</span>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-medium text-foreground">{r.customer?.name ?? '—'}</div>
          <div className="font-mono text-[10.5px] text-muted-foreground">{r.customer?.code ?? '—'}</div>
        </div>
      ),
    },
    {
      key: 'lines',
      header: 'Lines',
      align: 'right',
      width: '70px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] tabular-nums text-muted-foreground">
          {r.items.length || '—'}
        </span>
      ),
    },
    {
      key: 'reason',
      header: 'Reason',
      cell: (r) =>
        r.reason ? (
          <span className="text-[11.5px] text-muted-foreground">{r.reason}</span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground/60">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      cell: (r) => {
        const tone =
          r.status === 'APPROVED'
            ? 'success'
            : r.status === 'REJECTED'
              ? 'destructive'
              : 'warning';
        const label =
          r.status === 'APPROVED'
            ? 'Approved'
            : r.status === 'REJECTED'
              ? 'Rejected'
              : 'Pending review';
        return (
          <Badge variant={tone} className="text-[10.5px]">
            {label}
          </Badge>
        );
      },
    },
    {
      key: 'decision',
      header: 'Decided / assigned',
      width: '160px',
      cell: (r) => {
        if (r.status !== 'REQUESTED' && r.decidedAt) {
          return (
            <span className="text-[11.5px] text-muted-foreground">
              {fmt.relative(r.decidedAt)}
            </span>
          );
        }
        return r.assignedReviewerId ? (
          <span className="text-[11.5px] text-muted-foreground">
            assigned
            {r.assignedAt && (
              <span className="ml-1 text-[10.5px] text-muted-foreground/70">
                ({fmt.relative(r.assignedAt)})
              </span>
            )}
          </span>
        ) : (
          <span className="text-[11.5px] text-amber-600">Unassigned</span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '60px',
      hoverReveal: true,
      cell: (r) => (
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={() => setSelected(r)}
          aria-label="Open detail"
        >
          <ArrowUpRight className="h-3.5 w-3.5" />
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <ListPageHeader
        title="Return Requests"
        count={counts.all}
        description="Customer-initiated return requests. Pending rows are awaiting your reviewer's decision; approved + rejected rows stay here as the request-cycle audit trail (physical movement after approval lives on Return Orders)."
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Pending review"
          value={fmt.number(summary.pending)}
          tone={summary.pending ? 'warning' : 'default'}
          icon={ShieldQuestion}
        />
        <KpiCard label="Unassigned" value={fmt.number(summary.unassigned)} tone={summary.unassigned ? 'warning' : 'default'} icon={Undo2} />
        <KpiCard label="Decided (approved + rejected)" value={fmt.number(summary.decidedTotal)} tone="info" icon={CheckCircle2} />
        <KpiCard
          label="Oldest pending"
          value={summary.oldest ? fmt.relative(summary.oldest) : '—'}
          tone="default"
          icon={XCircle}
        />
      </div>

      <StatusTabs value={tab} onChange={(v) => setTab(v as RequestTab)} options={tabs} />

      <div className="flex items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search return # or external ref …"
            className="h-8 pl-8 text-[13px]"
          />
        </div>
      </div>

      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          isLoading={isLoading}
          onRowClick={setSelected}
          density="compact"
          emptyTitle="No return requests pending"
          emptyDescription="When a customer asks to return an order via the portal, the request lands here for your reviewer's decision."
          emptyAction={
            <Badge variant="muted" className="text-[11px]">
              All clear
            </Badge>
          }
        />
      )}

      <ReturnRequestDetailSheet
        returnRequest={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
      />
    </div>
  );
}
